import React, { Component } from 'react';
import '../App.css';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';

import { solarColorArray, windColorArray } from '../colorArrays.js';
import { timeplotLocale } from '../constants.js';

import * as d3 from 'd3';
import { TimeSeries } from 'pondjs';
import {
  Charts,
  ChartContainer,
  ChartRow,
  YAxis,
  LineChart,
  Resizable,
  styler,
} from 'react-timeseries-charts';

const styles = (theme) => ({
  progress: {
    margin: theme.spacing(2),
  },
  container: {
    marginBottom: '40px',
    fontFamily: 'Flanders Art Sans!important',
    width: '100%',
    height: '400px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      height: '300px',
      marginBottom: '100px',
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      height: '350px',
      marginBottom: '30px',
    },
    [theme.breakpoints.up('tablet')]: {
      height: '350px',
      marginBottom: '40px',
    },
  },
  chartDiv: {
    width: 'calc(100% - 200px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: '10px',
    },
  },
  rightHandLegend: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: '20px',
      height: '50px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '180px',
      height: '300px',
    },
  },
  rightHandLegendText: {
    textAlign: 'left',
  },
  cellAllTechs: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: '130px',
    },
  },
  plotHeader: {
    fontSize: '1.1em',
    fontWeight: 700,
    width: '100%',
    textAlign: 'left',
    marginTop: 0,
    marginBottom: 0,
    paddingTop: '10px',
    paddingBottom: '10px',
  },

  chart: {
    height: '300px',
    maxHeight: '300px',
    [theme.breakpoints.up('xs')]: {
      width: '320px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '400px',
    },
    [theme.breakpoints.up('tablet')]: {
      width: '568px',
    },
    [theme.breakpoints.up('md')]: {
      width: '422px',
      height: '300px',
      maxHeight: '300px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '635px',
    },
    [theme.breakpoints.up('lg1356')]: {
      width: '693px',
    },
    [theme.breakpoints.up('lg1440')]: {
      width: '742px',
    },
    [theme.breakpoints.up('lg1536')]: {
      width: '806px',
    },
    [theme.breakpoints.up('lg1660')]: {
      width: '889px',
    },
    [theme.breakpoints.up('xl')]: {
      width: '1062px',
    },
  },
  eventChart: {
    position: 'relative',
    marginTop: '10px',
    marginLeft: '60px',
    minHeight: '5px',
    maxHeight: '20px',
    [theme.breakpoints.up('xs')]: {
      width: '260px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '340px',
    },
    [theme.breakpoints.up('tablet')]: {
      width: '508px',
    },
    [theme.breakpoints.up('md')]: {
      width: '350px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '580px',
    },
    [theme.breakpoints.up('lg1536')]: {
      width: '746px',
    },
    [theme.breakpoints.up('lg1660')]: {
      width: '746px',
    },
    [theme.breakpoints.up('xl')]: {
      width: '1002px',
    },
  },
  indicatorTooltipContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginRight: '10px',
  },
  toolTipIconButton: {
    width: '24px',
    height: '24px',
    alignSelf: 'center!important',
  },
  toolTip: {
    textAlign: 'left',
    color: '#888!important',
    backgroundColor: '#aaaaaa',
    fontFamily: 'Flanders Art Sans!important',
    minHeight: '50px',
  },
  topLegend: {
    fontFamily: 'Flanders Art Sans!important',
    textAlign: 'left',
    width: '100%!important',
    marginLeft: '50px',
    fontWeight: 600,
  },
  bottomLegend: {
    fontFamily: 'Flanders Art Sans!important',
  },
  timeplotLegend: {
    justifyContent: 'center',
  },
  legendBox: {
    borderRadius: '5px',
    border: '1px solid #8b7f7e',
    padding: '5px',
    display: 'inline-block',
    fontSize: '10pt',
  },
});

function compareByFinalValue(a, b) {
  if (a.finalValue < b.finalValue) {
    return -1;
  }
  if (a.finalValue > b.finalValue) {
    return 1;
  }
  return 0;
}

class TimePlot extends Component {
  state = {
    dataReady: false,
    componentUpdated: false,
    yAxisLabel: '',
    data: {
      solar: [],
      wind: [],
      windOffshore: [],
    },
    plotData: {},
    finalValues: null,
    currentTech: {
      solar: true,
      wind: false,
      windOffshore: false,
    },
    currentMode: 'realtime',
    currentFilter: 'energy',
    currentDateTime: new Date().getTime(),
    yScale: d3.scaleLinear().clamp(true),
    size: [400, 200],
    plotConfig: {
      unit: 'Wh',
      color: {
        solar: '#fce0d4',
        wind: '#1eb0e6',
        windOffshore: '#1eb0e6',
      },
    },
    timeSeriesChart: {
      highlight: null,
      selection: null,
      scheme: 'Paired',
      series: [],
      columnNames: [],
      seriesUpdated: true,
    },

    tracker: null,
    trackerValue: 0,
    trackerEvent: null,

    hasForecast: false,
    hasMixedForecast: false,

    yAxisWidth: 60,

    graphDisplay: {
      numberDaysDisplayed: 8,
      daySliceFactor: 1,
      numberYearsDisplayed: 8,
      yearSliceFactor: 1,
    },
  };

  componentDidUpdate() {
    if (this.props.dataReady) {
      if (!this.state.componentUpdated) this.updatePlotData();
      else {
        let stateupdated =
          JSON.stringify(this.props.currentTech) ===
            JSON.stringify(this.state.currentTech) &&
          JSON.stringify(this.props.graphDisplay) ===
            JSON.stringify(this.state.graphDisplay) &&
          this.props.currentMode === this.state.currentMode &&
          this.props.currentFilter === this.state.currentFilter &&
          this.props.currentDateTime === this.state.currentDateTime &&
          this.props.data === this.state.data;
        if (!stateupdated) {
          this.setState({ componentUpdated: false });
        }
      }
    } else {
      if (this.state.componentUpdated) {
        this.setState({ componentUpdated: false });
      }
    }
  }

  updatePlotData() {
    const name = this.props.graphType;

    let plotData = [];
    let pointsData = [];
    let columnNames = [];
    let yAxisLabel = '';

    let coveredDemandValues;
    coveredDemandValues = this.props.data.demandWeek.map(
      (demand, i) => {
        const productionTotal = this.props.data.productionWeekTotals[i];
        let coveredDemandValue = { ...productionTotal };
        coveredDemandValue.value *= 100;
        coveredDemandValue.value /= demand.value;
        return coveredDemandValue;
      },
    );
    let coveredYearlyDemandValues;
    coveredYearlyDemandValues = this.props.data.demandYear.map(
      (demand, i) => {
        const productionTotal = this.props.data.productionYearTotals[i];
        let coveredYearlyDemandValue = { ...productionTotal };
        coveredYearlyDemandValue.value *= 100;
        coveredYearlyDemandValue.value /= demand.value;
        return coveredYearlyDemandValue;
      },
    );

    switch (this.props.graphType) {
      case 'CoveredDemand':
        plotData = [
          {
            name: 'covereddemand',
            values: coveredDemandValues,
          },
        ];
        pointsData = coveredDemandValues;
        columnNames = ['covereddemand'];
        yAxisLabel = 'Invulling van de vraag [%]';
        break;
      case 'CoveredYearlyDemand':
        plotData = [
          {
            name: 'coveredyeardemand',
            values: coveredYearlyDemandValues,
          },
        ];
        pointsData = coveredYearlyDemandValues;
        columnNames = ['coveredyeardemand'];
        yAxisLabel = 'Invulling van de vraag [%]';
        break;
      case 'ProductionVsDemand':
        plotData = [
          {
            name: 'demandweek',
            values: this.props.data.demandWeek,
          },
          {
            name: 'productionweektotal',
            values: this.props.data.productionWeekTotals.slice(0, this.props.data.demandWeek.length-1),
          },
        ];
        pointsData = this.props.data.demandWeek;
        columnNames = ['demandweek', 'productionweektotal'];
        yAxisLabel = 'Totale stroomproductie [GWh]';
        break;
      case 'YearlyProductionVsDemand':
        plotData = [
          {
            name: 'demandyear',
            values: this.props.data.demandYear,
          },
          {
            name: 'productionyeartotal',
            values: this.props.data.productionYearTotals.slice(0, this.props.data.demandYear.length-1),
          },
        ];
        pointsData = this.props.data.demandYear;
        columnNames = ['demandyear', 'productionyeartotal'];
        yAxisLabel = 'Totale stroomproductie [GWh]';
        break;
      case 'InstalledCapacities':
        plotData = this.props.data.installedYear;
        pointsData = this.props.data.installedYear[0].values;
        columnNames = plotData.map((d) => d.name);
        yAxisLabel = 'Geïnstalleerd vermogen [GW]';
        break;
      case 'YearlyProduction':
        plotData = this.props.data.productionYear;
        pointsData = this.props.data.productionYear[0].values;
        columnNames = plotData.map((d) => d.name);
        yAxisLabel = 'Groenestroomproductie [GWh]';
        break;
      case 'DayAheadPrices':
        plotData = [
          {
            name: 'prices',
            values: this.props.data.pricesWeek,
          },
        ];
        pointsData = this.props.data.pricesWeek;
        columnNames = plotData.map((d) => d.name);
        yAxisLabel = '€/MWh';
        break;
      case 'Production':
      default:
        plotData = this.props.data.productionWeek;
        pointsData = this.props.data.productionWeek[0].values;
        columnNames = plotData.map((d) => d.name);
        yAxisLabel = 'Groenestroomproductie [GWh]';
    }
    const columns = ['time', ...columnNames];

    const sliceFactor =
      this.props.currentMode === 'realtime'
        ? this.props.graphDisplay.daySliceFactor
        : this.props.graphDisplay.yearSliceFactor;
    const startingIndex = Math.floor(pointsData.length * (1 - sliceFactor));

    const currentMonth = parseInt(new Date().getMonth());
    const numberOfMonths =
      12 * (this.props.graphDisplay.numberYearsDisplayed - 1) +
      (currentMonth > 1 ? currentMonth - 1 : 12);

    if (this.props.currentMode === 'realtime') {
      pointsData = pointsData.slice(startingIndex);
    } else {
      pointsData = pointsData.slice(
        startingIndex,
        startingIndex + numberOfMonths,
      );
    }

    let points = [];
    for (let i = 0; i < pointsData.length; i++) {
      const t = pointsData[i].datetime;
      const point = [t];
      plotData.map((d) => {
        const dValueStartingIndex = Math.floor(
          d.values.length * (1 - sliceFactor),
        );
        let dValues;
        if (this.props.currentMode === 'realtime') {
          dValues = d.values.slice(dValueStartingIndex);
        } else {
          dValues = d.values.slice(
            dValueStartingIndex,
            dValueStartingIndex + numberOfMonths,
          );
        }
        point.push(dValues[i]?.value);
        return true;
      });
      points.push(point);
    }

    let finalValues = plotData.map((d) => {
      return {
        plot: d.name,
        finalValue: d.values[d.values.length - 1].value,
      };
    });
    finalValues = finalValues.sort(compareByFinalValue);
    finalValues = finalValues.reverse();
    const series = new TimeSeries({ name, columns, points });

    this.setState((previousState, props) => {
      return {
        dataReady: true,
        componentUpdated: true,
        currentDateTime: this.props.currentDateTime,
        currentTech: this.props.currentTech,
        currentMode: this.props.currentMode,
        currentFilter: this.props.currentFilter,

        yAxisLabel: yAxisLabel,
        data: this.props.data,
        plotData: plotData,
        finalValues: finalValues,
        timeSeriesChart: {
          ...previousState.timeSeriesChart,
          series: series,
          columnNames: columnNames,
          seriesUpdated: true,
        },
        tracker: this.props.currentDateTime,
        trackerValue: this.props.trackerValue,
        graphDisplay: this.props.graphDisplay,
      };
    });
  }

  render() {
    const { classes } = this.props;

    if (
      !this.props.dataReady ||
      !this.state.dataReady ||
      !this.state.componentUpdated
    ) {
      return (
        <div className={classes.chartDiv}>
          <CircularProgress className={classes.progress} size={50} />
        </div>
      );
    }
    const locale = timeplotLocale;
    const localeOption = 'nl-BE';

    const cols = this.state.timeSeriesChart.columnNames;
    let maxSeries = 0;
    if (this.state.dataReady) {
      for (let i = 0; i < this.state.plotData.length; i++) {
        let maxPlotData = d3.max(this.state.plotData[i].values, function (t) {
          return t.value;
        });
        if (maxPlotData > maxSeries) maxSeries = maxPlotData;
      }
    }
    let max = 1;
    let maxOrder = 1;
    let yLabelOffset = -5;
    let yAxisFormat = 'd';

    if (maxSeries > 1) {
      max = Math.pow(10, Math.ceil(Math.log10(maxSeries)));
      maxOrder = max / 10;
      while (maxSeries < max - 2 * maxOrder) max = max - 2 * maxOrder;
      if (max < 5) {
        yAxisFormat = (number) => {
          return number.toLocaleString('nl-BE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };
      } else {
        if (max < 10)
          yAxisFormat = (number) => {
            return number.toLocaleString('nl-BE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          };
      }
    } else {
      max = Math.pow(10, Math.floor(Math.log10(maxSeries)));
      yAxisFormat = (number) => {
        return number.toLocaleString('nl-BE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      };
      do {
        max = max * 2;
      } while (maxSeries > max);

      if (max < 0.5)
        yAxisFormat = (number) => {
          return number.toLocaleString('nl-BE', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          });
        };
    }

    const interpolationType = 'curveCatmullRom';
    const legendLabelStyle = {
      fontSize: 'normal',
      color: '#ffffff!important',
      solar: { fontSize: 'normal', color: '#ffffff' },
      wind: { fontSize: 'normal', color: '#ffffff' },
      windOffshore: { fontSize: 'normal', color: '#ffffff' },
      normal: { fontSize: 'normal', color: '#ffffff' },
      highlighted: { fontSize: '1.2em', color: '#ffffff' },
      selected: { fontSize: '1.2em', color: '#ffffff' },
      muted: { fontSize: '1.2em', color: '#ffffff' },
    };

    const legendStyle = styler([
      {
        key: 'solar',
        color: solarColorArray[5],
        width: 4,
        labelStyle: { color: '#ffffff' },
        paddingLeft: '20px',
        line: {
          normal: { stroke: solarColorArray[5], fill: 'none', strokeWidth: 2 },
          highlighted: {
            stroke: solarColorArray[6],
            fill: 'none',
            strokeWidth: 3,
          },
        },
      },
      {
        key: 'wind',
        color: windColorArray[5],
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'windOffshore',
        color: '#7ad8de',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'demandweek',
        color: 'black',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'demandyear',
        color: 'black',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'productionweektotal',
        color: '#1a5924',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'productionyeartotal',
        color: '#1a5924',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'covereddemand',
        color: 'grey',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'coveredyeardemand',
        color: 'grey',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
      {
        key: 'prices',
        color: 'grey',
        width: 4,
        labelStyle: { fontSize: 'normal', color: '#ffffff' },
      },
    ]);

    const series = this.state.timeSeriesChart.series;

    let observedCategories;

    let observedSolarCategory = {
      key: 'solar',
      label: 'door zonnepanelen',
      symbolType: 'dot',
    };
    let observedWindCategory = {
      key: 'wind',
      label: 'door onshore windturbines',
      symbolType: 'dot',
    };
    let observedWindOffshoreCategory = {
      key: 'windOffshoreForecast',
      label: 'door offshore windturbines',
      symbolType: 'dot',
    };

    if (this.props.currentTech.solar) {
      if (this.props.currentTech.wind) {
        if (this.props.currentTech.windOffshore) {
          observedCategories = [
            observedSolarCategory,
            observedWindCategory,
            observedWindOffshoreCategory,
          ];
        } else {
          observedCategories = [observedSolarCategory, observedWindCategory];
        }
      } else {
        if (this.props.currentTech.windOffshore) {
          observedCategories = [
            observedSolarCategory,
            observedWindOffshoreCategory,
          ];
        } else {
          observedCategories = [observedSolarCategory];
        }
      }
    } else {
      if (this.props.currentTech.wind) {
        if (this.props.currentTech.windOffshore) {
          observedCategories = [
            observedWindCategory,
            observedWindOffshoreCategory,
          ];
        } else {
          observedCategories = [observedWindCategory];
        }
      } else {
        observedCategories = [observedWindOffshoreCategory];
      }
    }

    let perMonth;
    let inLabel;
    let observedTitle;
    let production;
    let solar;
    let windOnshore;
    let windOffshore;
    switch (this.props.lang) {
      case 'nl':
      default:
        perMonth = ' per maand';
        inLabel = ', in';
        observedTitle = 'Waargenomen productie';
        production = 'productie';
        solar = 'zonnepanelen';
        windOnshore = 'onshore windturbines';
        windOffshore = 'offshore windturbines';
    }

    let selectedSources;
    switch (this.props.lang) {
      case 'nl':
      default:
        selectedSources = this.state.currentTech.solar
          ? this.state.currentTech.wind
            ? this.state.currentTech.windOffshore
              ? 'zonnepanelen, onshore en offshore windturbines'
              : 'zonnepanelen en onshore windturbines'
            : this.state.currentTech.windOffshore
              ? 'zonnepanelen en offshore windturbines'
              : 'zonnepanelen'
          : this.state.currentTech.wind
            ? this.state.currentTech.windOffshore
              ? 'onshore en offshore windturbines'
              : 'onshore windturbines'
            : 'offshore windturbines';
    }

    let currentPlotValue = 0;
    let previousValue = 0;
    const currentDateTime = this.props.currentDateTime;
    this.state.plotData.map((data) => {
      let currentValuePerPlot = data.values.filter(
        (obj) => obj.datetime.getTime() === currentDateTime.getTime(),
      )[0];
      if (currentValuePerPlot != null) {
        // 20181208 there is an overlap to make the curves meet between realtime and forecast, at this point the values do not need to be summed
        if (previousValue !== currentValuePerPlot.value) {
          currentPlotValue += currentValuePerPlot.value;
        }
        previousValue = currentValuePerPlot.value;
      }
      return true;
    });
    if (currentPlotValue < 0.01)
      currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 3,
      });
    else {
      if (currentPlotValue < 5) {
        currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });
      } else {
        if (currentPlotValue < 50) {
          currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
        } else {
          currentPlotValue = currentPlotValue.toLocaleString(localeOption, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
        }
      }
    }

    const yAxisLabel = this.state.yAxisLabel;

    let rightHandLegendText = null;
    let rightHandLegendTextColor = null;
    let rightHandLegendContent = null;
    let paddingTop = 0;
    let legendHeight = 300;
    let rightHandLegend = null;

    legendHeight = (300 * this.state.finalValues[0].finalValue) / max;
    //paddingTop=300 * (1 - this.state.finalValues[0].finalValue/ max) - 20
    paddingTop = 300 - legendHeight - 20;

    if (this.state.finalValues.length === 1) {
      switch (this.state.finalValues[0].plot) {
        case 'solar':
          rightHandLegendText = solar;
          rightHandLegendTextColor = solarColorArray[5];
          break;
        case 'wind':
          rightHandLegendText = windOnshore;
          rightHandLegendTextColor = windColorArray[5];
          break;
        case 'windOffshore':
          rightHandLegendText = windOffshore;
          rightHandLegendTextColor = '#7ad8de';
          break;
        case 'covereddemand':
          rightHandLegendText =
            'elektriciteitsvraag ingevuld door ' + selectedSources;
          rightHandLegendTextColor = 'grey';
          break;
        case 'coveredyeardemand':
          rightHandLegendText =
            'elektriciteitsvraag ingevuld door ' + selectedSources;
          rightHandLegendTextColor = 'grey';
          break;
        case 'prices':
          rightHandLegendText = 'Elektriciteitsprijs op de groothandelsmarkt';
          rightHandLegendTextColor = 'grey';
          break;
        default:
          rightHandLegendText = '';
          rightHandLegendTextColor = '#ffffff';
      }
      if (this.props.windowWidth >= 600) {
        rightHandLegendContent = (
          <div
            className={classes.rightHandLegend}
            style={{
              height: legendHeight + 'px',
              justifyContent: 'flex-start',
            }}
          >
            <div
              className={classes.rightHandLegendText}
              style={{ color: rightHandLegendTextColor }}
            >
              {rightHandLegendText}
            </div>
          </div>
        );
        rightHandLegend = (
          <div
            style={{
              display: 'block',
              height: '300px',
              paddingTop: paddingTop.toString() + 'px',
            }}
          >
            {rightHandLegendContent}
          </div>
        );
      } else {
        rightHandLegend = (
          <div
            className={classes.rightHandLegendText}
            style={{
              color: rightHandLegendTextColor,
              paddingTop: '10px',
              textAlign: 'center',
            }}
          >
            {rightHandLegendText}
          </div>
        );
      }
    } else {
      let legendBlocks = [];
      let iterator;
      let heightDifference;
      let bottomMargin;
      for (iterator = 0; iterator < this.state.finalValues.length; iterator++) {
        if (iterator < this.state.finalValues.length - 1) {
          heightDifference =
            ((this.state.finalValues[iterator].finalValue -
              this.state.finalValues[iterator + 1].finalValue) *
              300) /
            max;
          if (heightDifference < 20) {
            bottomMargin = 0;
          } else {
            bottomMargin = heightDifference - 20;
          }
        } else {
          bottomMargin = 0;
        }
        switch (this.state.finalValues[iterator].plot) {
          case 'solar':
            rightHandLegendText = solar;
            rightHandLegendTextColor = solarColorArray[5];
            break;
          case 'wind':
            rightHandLegendText = windOnshore;
            rightHandLegendTextColor = windColorArray[5];
            break;
          case 'windOffshore':
            rightHandLegendText = windOffshore;
            rightHandLegendTextColor = '#7ad8de';
            break;

          case 'demandweek':
            rightHandLegendText = 'elektriciteitsvraag';
            rightHandLegendTextColor = 'black';
            break;
          case 'productionweektotal':
            rightHandLegendText = selectedSources;
            rightHandLegendTextColor = '#1a5924';
            break;
          case 'demandyear':
            rightHandLegendText = 'elektriciteitsvraag';
            rightHandLegendTextColor = 'black';
            break;
          case 'productionyeartotal':
            rightHandLegendText = selectedSources;
            rightHandLegendTextColor = '#1a5924';
            break;

          default:
            rightHandLegendText = '';
            rightHandLegendTextColor = '#ffffff';
        }
        legendBlocks.push({
          color: rightHandLegendTextColor,
          marginBottom: bottomMargin,
          text: rightHandLegendText,
        });
      }
      if (this.props.windowWidth >= 600) {
        rightHandLegendContent = (
          <div
            className={classes.rightHandLegend}
            style={{
              height: legendHeight + 'px',
              justifyContent: 'flex-start',
            }}
          >
            {legendBlocks.map((legend, i) => (
              <div
                key={i}
                className={classes.rightHandLegendText}
                style={{
                  color: legend.color,
                  marginBottom: legend.marginBottom,
                }}
              >
                {legend.text}
              </div>
            ))}
          </div>
        );
        rightHandLegend = (
          <div
            style={{
              display: 'block',
              height: '300px',
              paddingTop: paddingTop.toString() + 'px',
            }}
          >
            {rightHandLegendContent}
          </div>
        );
      } else {
        rightHandLegend = (
          <div
            style={{
              display: 'block',
              textAlign: 'center',
              paddingTop: this.props.windowWidth >= 600 ? '20px' : '0px',
              marginBottom:
                this.props.windowWidth >= 600
                  ? 0
                  : this.props.currentMode === 'realtime'
                    ? '10px'
                    : 0,
            }}
          >
            {legendBlocks.map((legend, i) => (
              <div key={i} style={{ color: legend.color }}>
                {legend.text}
              </div>
            ))}
          </div>
        );
      }
    }

    let chartRowHeight = 300;
    let windOffshoreEvents = [];
    if (
      this.props.graphType === 'InstalledCapacities' &&
      this.state.currentTech.windOffshore
    ) {
      windOffshoreEvents = [
        {
          year: 2014,
          month: 4,
          event: 'Northwind',
        },
        {
          year: 2017,
          month: 1,
          event: 'Nobelwind',
        },
        {
          year: 2018,
          month: 6,
          event: 'Rentel',
        },
        {
          year: 2019,
          month: 4,
          event: 'Norther',
        },
        {
          year: 2020,
          month: 3,
          event: 'Northwester',
        },
        {
          year: 2020,
          month: 8,
          event: 'Seamade 12',
        },
      ].filter(
        (event) =>
          event.year >=
          (this.props.windowWidth >= 1024
            ? 2014
            : parseInt(d3.timeFormat('%Y')(new Date())) - 3),
      );
    }

    const currentYear = parseInt(new Date().getFullYear());
    const currentMonth = parseInt(new Date().getMonth());

    // notice that we say currentMonth -1 because the currentMonth returns values from 0 to 11
    const numberOfMonths =
      12 * (this.props.graphDisplay.numberYearsDisplayed - 1) +
      (currentMonth > 1 ? currentMonth - 1 : 12);
    const startingYear =
      this.props.finalYear - this.props.graphDisplay.numberYearsDisplayed + 1;
    let eventLegend = windOffshoreEvents.length > 0 && (
      <div className={classes.eventChart}>
        {windOffshoreEvents.map((event, i) => {
          let factor =
            (12 * (event.year - startingYear) + event.month - 1) /
            numberOfMonths;
          let eventHeight =
            -(this.props.windowWidth >= 960 ? 115 : 125) -
            (this.state.plotData.filter(
              (plot) => plot.name === 'windOffshore',
            )[0].values[12 * (event.year - startingYear) + (event.month - 1)]
              ?.value *
              300) /
              max;
          if (this.props.windowWidth >= 960) {
            switch (i) {
              case 3:
                eventHeight += 60;
                break;
              case 5:
                eventHeight += 85;
                break;
              default:
            }
          } else {
            switch (i) {
              case 1:
                eventHeight += 60;
                break;
              case 2:
                eventHeight -= 20;
                break;
              case 3:
                eventHeight += 10;
                break;
              case 4:
                eventHeight -= 40;
                break;
              default:
            }
          }
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                display: 'flex',
                top: eventHeight + 'px',
                left: 'calc(' + factor + ' * 100%)',
                fontSize: '0.7em',
                minHeight: i % 2 === 1 ? '35px' : '60px',
                height:
                  i % 2 === 1
                    ? i > 2
                      ? i > 3
                        ? '45px'
                        : '40px'
                      : '35px'
                    : '60px',
                alignItems:
                  i % 2 === 1
                    ? i > 2
                      ? i > 3
                        ? 'flex-end'
                        : '40px'
                      : 'center'
                    : '',
                borderLeft: 'black solid 1px',
                paddingLeft: '5px',
                width: '80px',
                maxWidth: '80px',
                textAlign: 'left',
              }}
            >
              {event.event} *
            </div>
          );
        })}
        * in gebruik genomen
      </div>
    );

    let formattedDates = [];
    let endDate = new Date(this.props.selectedDate);
    let counter;
    let widthPercent;

    if (this.props.currentMode === 'realtime') {
      for (
        counter = 1 - this.props.graphDisplay.numberDaysDisplayed;
        counter < 0;
        counter++
      ) {
        formattedDates.push(
          d3.timeFormat('%a %d.%m')(d3.timeDay.offset(endDate, counter)),
        );
      }
      formattedDates.push(d3.timeFormat('%a %d.%m')(endDate));
      widthPercent = 100 / this.props.graphDisplay.numberDaysDisplayed;
    } else {
      for (
        counter =
          this.props.finalYear -
          this.props.graphDisplay.numberYearsDisplayed +
          1;
        counter <= this.props.finalYear;
        counter++
      ) {
        formattedDates.push(counter.toString());
      }
    }

    let formattedDateLegend =
      this.props.currentMode === 'realtime' ? (
        <div
          className={classes.chart}
          style={{ maxHeight: '1em', fontSize: '0.9em', marginTop: '1em' }}
        >
          <Grid
            container
            style={{
              paddingLeft: '60px',
            }}
          >
            {formattedDates.map((formattedDate, i) => (
              <div
                key={i}
                style={{ display: 'inline-block', width: widthPercent + '%' }}
              >
                {formattedDate}
              </div>
            ))}
          </Grid>
        </div>
      ) : (
        <div
          className={classes.chart}
          style={{ maxHeight: '1em', fontSize: '0.9em', marginTop: '1em' }}
        >
          <Grid
            container
            style={{ marginLeft: '60px', width: `calc(100% - 60px)` }}
          >
            {formattedDates.map((formattedDate, i) => {
              let multiplier;
              switch (parseInt(formattedDate)) {
                case currentYear:
                  multiplier = currentMonth > 1 ? currentMonth - 1 : 12;
                  break;
                default:
                  multiplier = 12;
              }
              return (
                <div
                  key={i}
                  style={{
                    display: 'inline-block',
                    width: `calc(${multiplier}*(100%)/${numberOfMonths})`,
                  }}
                >
                  {parseInt(formattedDate) !== currentYear && formattedDate}
                </div>
              );
            })}
          </Grid>
        </div>
      );

    return (
      <div
        className={
          classes.container +
          ' ' +
          this.state.currentMode +
          (this.props.windowWidth < 960 ? ' narrow' : '') +
          (this.props.windowWidth < 600 &&
          this.props.currentTech.solar &&
          this.props.currentTech.wind &&
          this.props.currentTech.windOffshore
            ? ' ' + classes.cellAllTechs
            : '')
        }
      >
        <div className={classes.chartDiv}>
          <Resizable
            className={classes.chart}
            style={{
              margin: 0,
            }}
          >
            <ChartContainer
              timeRange={series.range()}
              format={
                this.state.currentMode === 'realtime'
                  ? //d3.timeFormat('%H')
                    (d) => {
                      if (d.getHours() === 0) {
                        return '';
                      }

                      if (d.getHours() === 12) {
                        return d.getHours();
                      }

                      return d.getHours();
                    }
                  : (d) => {
                      //return d.getHours();
                      if (d.getMonth() === 0) {
                        //return "|";
                        return '';
                      } else {
                        if (d.getMonth() % 3 === 0) {
                          return [
                            'J',
                            'F',
                            'M',
                            'A',
                            'M',
                            'J',
                            'J',
                            'A',
                            'S',
                            'O',
                            'N',
                            'D',
                          ][d.getMonth()];
                        }
                      }
                      return '';
                    }
              }
              showGrid={true}
              showGridPosition="under"
              // 20190408 updated to set the correct number of ticks

              // TODO should be made in dependence of number of years shown
              timeAxisTickCount={
                this.state.currentMode === 'realtime'
                  ? this.props.windowWidth < 960
                    ? 16
                    : 32
                  : this.state.data.times.length
              }
              timeAxisStyle={{
                class: 'x axis realtime',
                label: {},
                values: {
                  'font-size': 14,
                  'font-family': 'Flanders Art Sans',
                  color: '#8b7f7e',
                  fill: '#8b7f7e',
                },
                ticks: {},
                axis: {},
              }}
            >
              <ChartRow height={chartRowHeight}>
                <YAxis
                  id="value"
                  label={yAxisLabel}
                  labelOffset={yLabelOffset}
                  min={0}
                  max={max}
                  width={60}
                  type="linear"
                  style={{
                    label: {
                      fontWeight: 400,
                      'font-size': 18,
                      'font-family': 'Flanders Art Sans',
                    },
                    values: {
                      'font-size': 14,
                      'font-family': 'Flanders Art Sans',
                      color: '#8b7f7e',
                      fill: '#8b7f7e',
                    },
                    ticks: {},
                    axis: {},
                  }}
                  format={yAxisFormat}
                  tickCount={5}
                />

                <Charts>
                  <LineChart
                    axis="value"
                    style={legendStyle}
                    series={series}
                    breakArea={false}
                    columns={cols}
                    stack={false}
                    fillOpacity={0.2}
                    opacity={0.2}
                    interpolation={interpolationType}
                  ></LineChart>
                </Charts>
              </ChartRow>
            </ChartContainer>
          </Resizable>
          {formattedDateLegend}
          {eventLegend}
        </div>
        {rightHandLegend}
      </div>
    );
  }
}

TimePlot.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(TimePlot);
